/**
 * Centralized ZIP code to state detection utility.
 * Uses 3-digit prefixes for ambiguous border ranges (TN/MS, GA/FL, etc.)
 * then falls back to 2-digit prefix lookup.
 */

// 3-digit prefixes for ambiguous ZIP ranges shared between states
const ZIP_3_DIGIT: Record<string, string> = {
  // TN/MS border: 380-385 = TN, 386-397 = MS
  "380": "TN", "381": "TN", "382": "TN", "383": "TN", "384": "TN", "385": "TN",
  "386": "MS", "387": "MS", "388": "MS", "389": "MS",
  "390": "MS", "391": "MS", "392": "MS", "393": "MS", "394": "MS", "395": "MS", "396": "MS", "397": "MS",
  // GA/FL border: 320-322 = FL (not GA), 323-325 = FL, 326-329 = FL
  "300": "GA", "301": "GA", "302": "GA", "303": "GA", "304": "GA", "305": "GA",
  "306": "GA", "307": "GA", "308": "GA", "309": "GA",
  "310": "GA", "311": "GA", "312": "GA", "313": "GA", "314": "GA", "315": "GA",
  "316": "GA", "317": "GA", "318": "GA", "319": "GA",
  "320": "FL", "321": "FL", "322": "FL", "323": "FL", "324": "FL", "325": "FL",
  "326": "FL", "327": "FL", "328": "FL", "329": "FL",
  // NM/TX border: 880-884 = NM (not NV), 885 = TX (El Paso area)
  "880": "NM", "881": "NM", "882": "NM", "883": "NM", "884": "NM",
  // PA/DE: 190-196 = PA, 197-199 = DE
  "190": "PA", "191": "PA", "192": "PA", "193": "PA", "194": "PA", "195": "PA", "196": "PA",
  "197": "DE", "198": "DE", "199": "DE",
};

// Standard 2-digit prefix map
const ZIP_2_DIGIT: Record<string, string> = {
  // Service area states
  "60": "IL", "61": "IL", "62": "IL",
  "46": "IN", "47": "IN",
  "53": "WI", "54": "WI",
  "48": "MI", "49": "MI",
  // All other US states
  "00": "PR", "01": "MA", "02": "MA", "03": "NH", "04": "ME", "05": "VT",
  "06": "CT", "07": "NJ", "08": "NJ", "09": "AE",
  "10": "NY", "11": "NY", "12": "NY", "13": "NY", "14": "NY",
  "15": "PA", "16": "PA", "17": "PA", "18": "PA", "19": "DE",
  "20": "DC", "21": "MD", "22": "VA", "23": "VA", "24": "VA",
  "25": "WV", "26": "WV", "27": "NC", "28": "NC", "29": "SC",
  "30": "GA", "31": "GA", "32": "FL", "33": "FL", "34": "FL",
  "35": "AL", "36": "AL", "37": "TN", "38": "MS", "39": "MS",
  "40": "KY", "41": "KY", "42": "KY",
  "43": "OH", "44": "OH", "45": "OH",
  "50": "IA", "51": "IA", "52": "IA",
  "55": "MN", "56": "MN", "57": "SD", "58": "ND", "59": "MT",
  "63": "MO", "64": "MO", "65": "MO",
  "66": "KS", "67": "KS", "68": "NE", "69": "NE",
  "70": "LA", "71": "AR", "72": "AR",
  "73": "OK", "74": "OK",
  "75": "TX", "76": "TX", "77": "TX", "78": "TX", "79": "TX",
  "80": "CO", "81": "CO", "82": "WY", "83": "ID", "84": "UT",
  "85": "AZ", "86": "AZ", "87": "NM", "88": "NV", "89": "NV",
  "90": "CA", "91": "CA", "92": "CA", "93": "CA", "94": "CA", "95": "CA", "96": "CA",
  "97": "OR", "98": "WA", "99": "AK",
};

/**
 * Detect the US state from a ZIP code.
 * Uses 3-digit prefix first for ambiguous border ranges, then falls back to 2-digit.
 */
export const detectStateFromZip = (zip: string): string | null => {
  if (!zip || zip.length < 2) return null;
  
  // Try 3-digit prefix first for ambiguous ranges
  if (zip.length >= 3) {
    const prefix3 = zip.substring(0, 3);
    if (ZIP_3_DIGIT[prefix3]) return ZIP_3_DIGIT[prefix3];
  }
  
  // Fall back to 2-digit prefix
  const prefix2 = zip.substring(0, 2);
  return ZIP_2_DIGIT[prefix2] || null;
};

/**
 * Get all unique state abbreviations (for select dropdowns).
 */
export const getAllStates = (): string[] => {
  return Array.from(new Set(Object.values(ZIP_2_DIGIT))).sort();
};

/**
 * Valid pickup/origin states for service area validation.
 */
export const VALID_ORIGIN_STATES = ["IL", "IN", "WI", "MI"];

/**
 * Validate whether a ZIP code is within the service area.
 */
export const validateServiceArea = (zip: string): { valid: boolean; state: string | null; message?: string } => {
  const state = detectStateFromZip(zip);
  
  if (!state) {
    return { valid: false, state: null, message: "Unable to determine state from ZIP code" };
  }
  
  if (!VALID_ORIGIN_STATES.includes(state)) {
    return { valid: false, state, message: `We currently only service pickups from IL, IN, WI, and MI. ZIP ${zip} is in ${state}.` };
  }
  
  return { valid: true, state };
};
